"use client";

import React from "react";
import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Text } from "@/components/ui/text";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SectionsService } from "@/api-client/sections";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { ExternalLink } from "lucide-react";
import { getSourceDisplayName } from "@/lib/url-utils";

interface SignalDetailDrawerProps {
  signalId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SignalDetailDrawer: React.FC<SignalDetailDrawerProps> = ({
  signalId,
  open,
  onOpenChange,
}) => {
  const { data, isLoading, error } = useAuthenticatedQuery<{
    id: number;
    title: string;
    description: string;
    reasoning?: string;
    sources: Array<{
      name: string;
      url: string;
      type: string;
    }>;
  }>({
    queryKey: ["signalDetails", signalId],
    queryFn: () => {
      if (!signalId) throw new Error("Signal ID is required");
      return SectionsService.getSignalDetails(signalId);
    },
    enabled: open && signalId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Signal Details"
      side="right"
    >
      {isLoading && <DetailDrawerSkeleton />}
      
      {error ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Text size="base" weight="medium" color="muted">
            Failed to load signal details. Please try again.
          </Text>
        </div>
      ) : null}

      {!isLoading && data ? (
        <>
          {/* Title Section */}
          <div className="space-y-2 pb-4">
            <Text as="h3" size="xl" weight="semibold" color="default" className="leading-tight">
              {data.title}
            </Text>
          </div>

          <Separator className="my-4" />

          {/* Description Section */}
          <div className="space-y-3 pb-4">
            <Text as="h4" size="base" weight="semibold" color="default">
            Context
            </Text>
            <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
              {data.description}
            </Text>
          </div>

          {/* Rationale Section */}
          {data.reasoning && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 pb-4">
                <Text as="h4" size="base" weight="semibold" color="default">
                  Rationale
                </Text>
                <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                  {data.reasoning}
                </Text>
              </div>
            </>
          )}

          {/* Sources Section - Moved to last */}
          {data.sources && data.sources.length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3">
                <Text as="h4" size="base" weight="semibold" color="default">
                  Sources
                </Text>
                <div className="flex flex-wrap gap-2.5">
                  {data.sources.map((source, index) => {
                    const displayName = getSourceDisplayName(source);
                    return source.url ? (
                      <a
                        key={index}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center group"
                      >
                        <Badge 
                          variant="secondary" 
                          className="bg-secondary cursor-pointer hover:bg-secondary/80 transition-all duration-200 hover:shadow-md hover:scale-105 group-hover:gap-1.5 gap-1"
                        >
                          <Text size="xs" weight="medium" color="default">
                            {displayName}
                          </Text>
                          <ExternalLink className="w-3 h-3 ml-1.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                        </Badge>
                      </a>
                    ) : (
                      <Badge key={index} variant="secondary" className="bg-secondary">
                        <Text size="xs" weight="medium" color="default">
                          {displayName}
                        </Text>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      ) : null}
    </DetailDrawer>
  );
};

